import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import LocationMap from './components/LocationMap';
import InteractiveMap from './components/InteractiveMap';
import SlideToolbar from './components/SlideToolbar';
import ReloadPrompt from './components/ReloadPrompt';
import CompactMetrics from './components/CompactMetrics';
import { AirQualityLegend } from './components/AirQualityLegend';
import RegionSelector from './components/RegionSelector';
import { locationMap, getLocationIdsForRegion } from './config/locations';

import { calculateAdjustedPM25, calculateActualPM25 } from './utils/calculations';
import CountyPanel from './components/CountyPanel';

const IFRAME_HEIGHT = 2400;

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [modalPosition, setModalPosition] = useState(null);
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'historical'
  const [regionFilter, setRegionFilter] = useState({ town: null, zipCode: null });

  // Location ids matching the active area/zip filter. null => no filter (show all).
  const isRegionFiltered = Boolean(regionFilter.town || regionFilter.zipCode);
  const visibleLocationIds = isRegionFiltered
    ? getLocationIdsForRegion(regionFilter)
    : null;

  // Zip codes covered by the active filter (used to highlight + zoom the map).
  // A selected zip is itself; a selected town expands to all zips in that town.
  const activeZips = !isRegionFiltered
    ? null
    : regionFilter.zipCode
      ? [regionFilter.zipCode]
      : Array.from(new Set(
          visibleLocationIds.map(id => locationMap[id]?.zipCode).filter(Boolean)
        ));


  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/statistics`);
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      const data = await response.json();

      if (data.locations) {
        Object.keys(data.locations).forEach(locationId => {
          const locationInfo = locationMap[locationId];
          if (locationInfo) {
            const locationData = data.locations[locationId];
            // Recalculate averages excluding faulty sensors
            if (locationData.currentReadings) {
              const readings = Object.entries(locationData.currentReadings)
                .map(([sensorId, reading]) => ({
                  ...reading,
                  sensorId: Number(sensorId)
                }));
              
              locationData.hour = {
                ...locationData.hour,
                adjustedAverage: calculateAdjustedPM25(readings),
                rawAverage: calculateActualPM25(readings)
              };
            }

            data.locations[locationId] = {
              ...locationData,
              fullName: locationInfo.fullName,
              name: locationInfo.fullName,
              type: locationInfo.type
            };
          }
        });
      }

      setStatistics(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Fail silently - keep showing existing data if available
      if (!statistics) {
        setError('Failed to fetch air quality data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    const intervalId = setInterval(fetchStatistics, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationSelect = (locationId, event, elementRect) => {
    if (!locationId) {
      setSelectedLocation(null);
      setIsToolbarOpen(false);
      setModalPosition(null);
      return;
    }

    const position = {
      clickY: event?.pageY || 0,
      elementTop: elementRect?.top || event?.pageY - 10,
      elementHeight: elementRect?.height || 20
    };

    setModalPosition(position);

    const locationIdStr = locationId.toString();
    const locationInfo = locationMap[locationId];

    if (locationInfo) {
      setSelectedLocation({
        id: locationIdStr,
        name: locationInfo.fullName,
        fullName: locationInfo.fullName,
        shortName: locationInfo.shortName,
        type: locationInfo.type
      });
      setIsToolbarOpen(true);
    }
  };

  if (loading && !statistics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-50 overflow-hidden"
      style={{ height: IFRAME_HEIGHT }}
    >
      <ReloadPrompt onReload={fetchStatistics} />

      <div className="w-full max-w-[720px] mx-auto px-2 py-2">
        <div className="flex justify-between items-center mb-3">
          <h1 className="text-xl sm:text-2xl font-bold">Air Quality Monitoring Network</h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center">
            <AlertCircle className="text-red-500 mr-2" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-3">
          <CompactMetrics statistics={statistics} />
        </div>

        <div className="mb-3">
          <CountyPanel />
        </div>

        <div className="mb-3">
          <AirQualityLegend />
        </div>

        <div className="mb-3 bg-white rounded-lg shadow p-3">
          <div className="px-1 pt-1 pb-3">
            <RegionSelector
              value={regionFilter}
              onChange={setRegionFilter}
              matchCount={visibleLocationIds ? visibleLocationIds.length : Object.keys(locationMap).length}
            />
          </div>
          <InteractiveMap
            statistics={statistics}
            loading={loading}
            onLocationSelect={(id, event) => {
              const element = event.target.closest('.location-card');
              const rect = element?.getBoundingClientRect();
              handleLocationSelect(id, event, rect);
            }}
            selectedLocation={selectedLocation}
            setIsToolbarOpen={setIsToolbarOpen}
            isToolbarOpen={isToolbarOpen}
            visibleLocationIds={visibleLocationIds}
            activeZips={activeZips}
            onRegionSelect={setRegionFilter}
          />
        </div>

        <div className="mb-3">
          <LocationMap
            loading={loading}
            onLocationSelect={(id, event) => {
              const element = event.target.closest('.location-card');
              const rect = element?.getBoundingClientRect();
              handleLocationSelect(id, event, rect);
            }}
            statistics={statistics}
            locationMap={locationMap}
            selectedLocation={selectedLocation}
            isToolbarOpen={isToolbarOpen}
            visibleLocationIds={visibleLocationIds}
          />
        </div>

        <SlideToolbar
          isOpen={isToolbarOpen}
          onClose={() => {
            setIsToolbarOpen(false);
            setSelectedLocation(null);
            setModalPosition(null);
          }}
          locationData={selectedLocation}
          statistics={statistics}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          modalPosition={modalPosition}
          frameHeight={IFRAME_HEIGHT}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </div>
    </div>
  );
}

export default App;